/**
 * State for the guest-facing page of an external ("off-platform") booking offer.
 *
 * The bañista opens this link before having an account, so the page is
 * UNAUTHENTICATED and the opaque token is the only credential; the server returns a
 * redacted view of the offer.
 *
 * The page does NOT take the payment. It hands the offer to the normal checkout —
 * the bañista registers, pays there, and a real Sharetribe transaction is created
 * with reduced-commission line items. See docs/external-bookings.md.
 */

import { storableError } from '../../util/errors';
import { getExternalBookingByToken } from '../../util/api';
import { checkoutSetInitialValues } from '../CheckoutPage/checkoutSetInitialValues';
import { addMarketplaceEntities, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { formatGuestRangeLabel } from '../../util/externalBookings';
import { createImageVariantConfig } from '../../util/sdkLoader';

// ================ Action types ================ //

export const FETCH_OFFER_REQUEST = 'app/ExternalBookingPaymentPage/FETCH_OFFER_REQUEST';
export const FETCH_OFFER_SUCCESS = 'app/ExternalBookingPaymentPage/FETCH_OFFER_SUCCESS';
export const FETCH_OFFER_ERROR = 'app/ExternalBookingPaymentPage/FETCH_OFFER_ERROR';

// ================ Reducer ================ //

const initialState = {
  booking: null,
  fetchInProgress: false,
  fetchError: null,
};

export default function externalBookingPaymentPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_OFFER_REQUEST:
      return { ...state, fetchInProgress: true, fetchError: null };
    case FETCH_OFFER_SUCCESS:
      return { ...state, fetchInProgress: false, booking: payload };
    case FETCH_OFFER_ERROR:
      return { ...state, fetchInProgress: false, fetchError: payload };
    default:
      return state;
  }
}

// ================ Action creators ================ //

const fetchOfferRequest = () => ({ type: FETCH_OFFER_REQUEST });
const fetchOfferSuccess = booking => ({ type: FETCH_OFFER_SUCCESS, payload: booking });
const fetchOfferError = e => ({ type: FETCH_OFFER_ERROR, payload: e, error: true });

// ================ Thunks ================ //

export const fetchOffer = token => dispatch => {
  dispatch(fetchOfferRequest());
  return getExternalBookingByToken(token)
    .then(response => {
      dispatch(fetchOfferSuccess(response.booking));
      return response.booking;
    })
    .catch(e => {
      dispatch(fetchOfferError(storableError(e)));
    });
};

export const loadData = params => dispatch => dispatch(fetchOffer(params.token));

/**
 * Hand the offer over to the normal checkout.
 *
 * Reuses the guest → signup → checkout path that already exists for a logged-out
 * visitor booking from a listing (`ListingPage.shared.js:handleSubmit`): the order is
 * stashed in sessionStorage so it survives the signup round-trip.
 *
 * `externalBookingToken` travels in `orderData` and is the ONLY thing the client
 * sends about the price — the server resolves it and decides the money, exactly like
 * a discount code.
 *
 * @returns {Promise<Object|null>} the listing, or null when it can't be loaded
 */
export const prepareCheckout = (booking, isAuthenticated, config) => (dispatch, getState, sdk) => {
  // The checkout's DetailsSideCard keeps only the variants whose name starts with the
  // configured `variantPrefix`, and those are CUSTOM variants: asking for
  // `variants.listing-card` does nothing unless the request also declares it via
  // createImageVariantConfig. This used to ask for `landscape-crop`, which matched
  // neither — hence a checkout with no pool photo and no host avatar. The avatar needs
  // the `square-small` pair, which are default variants and need no declaration.
  // Mirrors ListingPage.duck's own `showListing` query.
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } =
    config?.layout?.listingImage || {};
  const aspectRatio = aspectHeight / aspectWidth;

  return sdk.listings
    .show({
      id: booking.listingId,
      include: ['author', 'author.profileImage', 'images'],
      'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
      'fields.image': [
        `variants.${variantPrefix}`,
        `variants.${variantPrefix}-2x`,
        'variants.square-small',
        'variants.square-small2x',
      ],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    })
    .then(response => {
      dispatch(addMarketplaceEntities(response));

      // CheckoutPage requires `listing.author.id` and bounces back to the listing
      // page without it, so the DENORMALISED entity is what has to be handed over —
      // the raw SDK resource only carries `relationships.author`. ListingPage does
      // the same via its `getListing`.
      const [listing] = getMarketplaceEntities(getState(), [
        { id: response.data.data.id, type: 'listing' },
      ]);
      if (!listing?.author?.id) {
        throw new Error('Listing could not be resolved for checkout');
      }

      const orderData = {
        bookingDates: {
          bookingStart: new Date(booking.bookingStart),
          bookingEnd: new Date(booking.bookingEnd),
        },
        // The tier key the rest of the app uses; the authoritative bounds live on the
        // offer and are re-read server-side.
        guestCount: formatGuestRangeLabel(booking).replace(/\s*personas?$/, ''),
        externalBookingToken: booking.token,
        // Already-agreed price: no other discount may stack on top of it.
        firstBookingDiscount: false,
      };

      dispatch(
        checkoutSetInitialValues(
          { listing, orderData, confirmPaymentError: null },
          !isAuthenticated
        )
      );
      return listing;
    })
    .catch(e => {
      dispatch(fetchOfferError(storableError(e)));
      return null;
    });
};
